import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import moment from 'moment';

const { TextArea } = Input;
const { Option } = Select;

interface HolidayModelProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void;
    initialValues?: {
        holiday_id?: number;
        holiday_name?: string;
        holiday_description?: string;
        holiday_start_date?: string;
        holiday_end_date?: string;
        holiday_type?: string;
    } | null;
    isEditMode?: boolean;
}

const HolidayModel: React.FC<HolidayModelProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };

    const handleAddHoliday = async (values: any) => {
        try {
            const dataVal = {
                holiday_name: values.holiday_name,
                holiday_description: values.holiday_description,
                holiday_type: values.holiday_type,
                holiday_start_date: values.holiday_start_date?.format('YYYY-MM-DD'),
                holiday_end_date: values.holiday_end_date?.format('YYYY-MM-DD'),
                ...(initialValues?.holiday_id && { holiday_id: initialValues.holiday_id }),
            };

            const url = isEditMode ? 'edit-holiday' : 'add-holiday';
            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });

            onCreate(data);
            showSuccess(isEditMode ? 'Holiday updated successfully!' : 'Holiday created successfully!');
            form.resetFields();

            if (isModalClose) {
                onCancel();
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} holiday`);
        }
    };

    useEffect(() => {
        if (visible && initialValues) {
            form.setFieldsValue({
                holiday_name: initialValues.holiday_name,
                holiday_description: initialValues.holiday_description,
                holiday_type: initialValues.holiday_type,
                holiday_start_date: initialValues.holiday_start_date
                    ? moment(initialValues.holiday_start_date, 'YYYY-MM-DD')
                    : null,
                holiday_end_date: initialValues.holiday_end_date
                    ? moment(initialValues.holiday_end_date, 'YYYY-MM-DD')
                    : null,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, initialValues, form]);

    return (
        <Modal
            title={isEditMode ? 'Edit Holiday' : 'Add New Holiday'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddHoliday}
                autoComplete="off"
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="holiday_name"
                            label="Holiday Name"
                            rules={[
                                { required: true, message: 'Please enter the holiday name' },
                                { max: 255, message: 'Maximum 255 characters allowed' },
                            ]}
                        >
                            <Input
                                showCount
                                maxLength={255}
                                placeholder="e.g., Festival Holiday"
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="holiday_type"
                            label="Holiday Type"
                            rules={[{ required: true, message: 'Please select holiday type' }]}
                        >
                            <Select placeholder="Select type">
                                <Option value="public">Public</Option>
                                <Option value="company">Company</Option>
                                <Option value="optional">Optional</Option>
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="holiday_start_date"
                            label="Start Date"
                            rules={[{ required: true, message: 'Please select start date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            name="holiday_end_date"
                            label="End Date"
                            rules={[{ required: true, message: 'Please select end date' }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>

                    <Col span={24}>
                        <Form.Item
                            name="holiday_description"
                            label="Description"
                            rules={[{ max: 500, message: 'Maximum 500 characters allowed' }]}
                        >
                            <TextArea rows={4} maxLength={500} showCount />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && (
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={() => setIsModalClose(false)}
                                disabled={isSubmitDisabled()}
                            >
                                Submit & Create New Holiday
                            </Button>
                        )}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default HolidayModel;
